import {Expressions} from '@packages/systems/wfdl';
import {
  updateExpressionsAction,
  type ExpressionEditorActionPayload,
} from '@packages/systems/ExpressionEditor-utils';
import {trim} from 'lodash';
import {toStringId} from '@packages/systems/core/utils/HtmlUtils';
import {
  ATTR_DATA_NAME_PATH,
  ATTR_ID_PATH,
  ATTR_NAME_PATH,
  FORM_DEFAULT_LABEL,
  FORM_NAME_PATH,
} from '../constants';
import {EText} from '@packages/systems/wfdl-ast';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {selectSelectedNodeNativeId} from '@packages/utilities/platform-selectors';
import {createAction} from '@packages/utilities/create-action';
import {INPUT_SETTINGS_FIELD_ID_CHANGED} from '@packages/systems/designer/actions';
import {useEffect, useState} from 'react';
import {NativeId} from '@packages/systems/core/utils/NativeIdUtils';
import {
  useDispatch,
  useDesignerSelector,
} from '@packages/systems/designer/flux';

const {getIn} = ContextualExpressions;

const inputIdChanged = createAction<
  typeof INPUT_SETTINGS_FIELD_ID_CHANGED,
  {inputNodeId: NativeId; attrId: string}
>(INPUT_SETTINGS_FIELD_ID_CHANGED);

const updateSiblingLabel = (
  fluxDispatch: (arg1?: any) => void,
  selectedNodeNativeId: string,
  attrId: string
) => {
  if (attrId && attrId.length !== 0) {
    fluxDispatch(inputIdChanged({inputNodeId: selectedNodeNativeId, attrId}));
  }
};

export const useUpdateSiblingLabel = (id: string) => {
  const fluxDispatch = useDispatch();
  const selectedNodeNativeId = useDesignerSelector(selectSelectedNodeNativeId);
  useEffect(() => {
    // If id changed, update sibling labels for attributes
    if (selectedNodeNativeId) {
      updateSiblingLabel(fluxDispatch, selectedNodeNativeId, id);
    }
  }, [id, fluxDispatch, selectedNodeNativeId]);
};

const updateIdNameAndNameAttr = (
  dispatch: (arg1?: any) => void,
  name: string
) => {
  const id = toStringId(name) || FORM_DEFAULT_LABEL;

  dispatch(
    updateExpressionsAction([
      {key: FORM_NAME_PATH, newExpression: EText(name)},
      {key: ATTR_DATA_NAME_PATH, newExpression: EText(name)},
      {key: ATTR_ID_PATH, newExpression: EText(id)},
      {key: ATTR_NAME_PATH, newExpression: EText(id)},
    ])
  );
};

export const useUpdateFormNameDependencies = (
  expr: ContextualExpression,
  dispatch: (arg1?: any) => void
): [ContextualExpression, (arg1: any) => any] => {
  const formNameExpr = getIn(expr, FORM_NAME_PATH);
  if (!formNameExpr) {
    throw Error(
      'Failed to find a form name in expression within input element'
    );
  }
  const [formNameExprState, setFormNameExprState] = useState(formNameExpr);

  const formNameValue =
    ContextualExpressions.getText(stripAnn(formNameExpr)) || '';

  useEffect(() => {
    setFormNameExprState(formNameExpr);
  }, [formNameValue]);

  // Hijack dispatch to update multiple fields
  const formNameOnChange = (action: ExpressionEditorActionPayload) => {
    if (action.type === 'EXPRESSION_ACTION') {
      const nextName = Expressions.getText(action.payload.newExpression) || '';
      const currentName = formNameValue || '';
      const trimmedNextName = trim(nextName);
      if (trimmedNextName === currentName) return;
      updateIdNameAndNameAttr(dispatch, trimmedNextName);
    } else if (action.type === 'EXPRESSION_RESET_TO_PRIMARY_LOCALE') {
      dispatch(action);
    } else {
      //todo bindings
    }
  };

  return [formNameExprState, formNameOnChange];
};
